<!-- This is used for US FTC (Payment, Switch Plan, Direct, Ex-sub) -->
<template>
    <div class="wrapper">
        <div class="padded-container--ftc">
            <div
                data-ci="summary-page"
                class="aa-layout payment-layout summary-page ftc-layout"
                :class="{ 'summary-page--signup-flow': isExSubscriber }"
                ref="paymentForm">
                <span v-if="isSubscriber" class="icon-fill-rounded__check"></span>
                <h3 :class="getTitleClass">
                    {{ getTitle }}
                </h3>
                <span
                    v-if="pageAttr.explainer_description_subtitle"
                    class="payment-layout__step-subtitle payment-layout__centered">
                    {{ pageAttr.explainer_description_subtitle }}
                </span>
                <div class="payment-layout__summary summary-page__details full-width">
                    <BillingAgreementSummary
                        :isFTC="isFTC"
                        @found-price="updatePrice"
                        :coupon="coupon"
                        :couponCheckPending="couponCheckPending"
                        :couponDisclaimer="couponDisclaimerCopy"
                        @applyCoupon="applyCoupon"
                        @updateOrder="updateOrder"
                        @trialEndDateFormatted="updateTrialEndDate"
                        ref="orderSummary"
                        :isFromDirect="isFromDirect"
                        :recurly="recurly"
                        :recurlyReady="recurlyReady"
                        :formConfig="orderSummaryConfig"
                        :promo="appliedCouponCode"
                        :pricing="pricing"
                        :pageAttr="pageAttr"
                        :nextNonZeroBillingPrice="nextNonZeroBillingPrice"
                        :showCheckbox="showCheckbox"
                        :couponDiscountBillingDate="
                            couponDiscountBillingDate
                        "></BillingAgreementSummary>
                    <div class="pinline" v-if="hasValidStoredPaymentMethod"></div>
                    <div :id="errorMessageElementId" role="alert">
                        <p v-if="formErrorMessage" class="error">
                            {{ formErrorMessage }}
                        </p>
                    </div>
                    <BillingPaymentMethod
                        :isFTC="isFTC"
                        :activeTab="activeTab"
                        @update:activeTab="emitActiveTab($event)"
                        :pageAttr="pageAttr"
                        @is-payment-method-form-edit="setPaymentMethodFormEnabled"
                        @update-active-tab="setActiveTab"
                        :handleProcessedPayment="handleProcessedPayment"
                        :errorMessageElementId="errorMessageElementId"
                        :twoButtonExperience="isSwitchPlan"
                        :pageTypeForTracking="pageTypeForTracking"
                        ref="paymentFormGranParent"></BillingPaymentMethod>
                    <SummaryUpgradeDowngrade :pageAttr="pageAttr" v-if="isSwitchPlan" />

                    <!-- <input type="hidden" name="recurly-token" data-recurly="token" /> -->
                    <div class="pinline" v-if="hasValidStoredPaymentMethod"></div>
                    <WaiveCoolingRights
                        :checkboxConfig="waiveOfCoolingRightsCheckboxConfig"
                        :errorMessage="waiveOfCoolingRighsCheckboxErrorMessage"
                        :disclaimer="pageAttr.aa_payment_agreement_copy_ftc"
                        :buttonConfig="submitConfig"
                        :buttonState="ftcSubmitButtonState"
                        :showCheckbox="showCheckbox"
                        :getLegalCopyForSwitchPlan="getLegalCopyForSwitchPlan"
                        :isSwitchPlan="isSwitchPlan"
                        @change="setWaiveOfCoolingRights"
                        @submit="subscribeOrUpdate($event)" />
                </div>
            </div>
        </div>
        <transition name="fade">
            <!-- Overlay for Switch Plan Edit Payment is handled by SharedPaymentComponent -->
            <WaitingOverlay
                :line1="getPaymentProcessingMessage"
                :line2="$getLocale('this_will_not_take_long')"
                v-if="transactionIsProcessing" />
        </transition>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapMutations } from 'vuex';

    import couponsMixin from 'aa/vue/plugins/couponsMixin';
    import checkoutMixin from 'aa/vue/plugins/checkoutMixin';
    import switchPlanMixin from 'aa/vue/components/multisubplanpicker/switchPlanMixin';
    import commonMixins from 'aa/vue/plugins/account/commonMixins';
    import pricePrettyPrint from 'aa/vue/plugins/priceMixin';
    import currentSubscriptionMixin from 'aa/vue/components/account/currentSubscriptionMixin';
    import FormState from 'aa/vue/plugins/FormState';
    import billingInfoMixin from './account/billingInfoMixin';

    import { OrderSummaryConfig, TAB } from 'aa/vue/FormConfig';
    import BillingAgreementSummary from 'aa/vue/components/BillingAgreementSummary';
    import SummaryUpgradeDowngrade from 'aa/vue/components/SummaryUpgradeDowngrade';
    import OrderSummary from 'aa/vue/components/OrderSummary';
    import BillingPaymentMethod from 'aa/vue/components/BillingPaymentMethod';
    import FormButton from 'atoms/FormButton';
    import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
    import {
        FTC_COMPLIANCE_CHECKBOX_ENABLED,
        PAYMENT_EXSUB_BILLING_SUMMARY,
    } from 'helpers/featureConstants';
    import { PAGE_TYPES } from 'services/Tracking';
    import {
        PAGE_TYPES as AA_PAGE_TYPES,
        ACTION_NAMES as AA_ACTION_NAMES,
    } from 'aa/vue/constants/aaTrackingConstants';
    import WaiveCoolingRights from './payment/WaiveCoolingRights';
    import { pluralizeDuration } from 'aa/helpers/stringManipulation';
    import { COUPON_TYPES } from 'aa/vue/models/CouponDiscount';
    import WaitingOverlay from 'aa/vue/atoms/WaitingOverlay';
    import { ButtonState } from 'models/ButtonConfig';
    import Product from 'aa/vue/models/Product';
    import { fetchStoredPaymentIfEmpty } from './payment/signupUtil';
    import { RECAPTCHA_FORMS } from 'recaptcha/Recaptcha';

    export default {
        name: 'SummaryFTC',
        mixins: [
            couponsMixin,
            pricePrettyPrint,
            checkoutMixin,
            commonMixins,
            switchPlanMixin,
            currentSubscriptionMixin,
            FormState,
            billingInfoMixin,
        ],
        components: {
            BillingAgreementSummary,
            BillingPaymentMethod,
            FormButton,
            SummaryUpgradeDowngrade,
            OrderSummary,
            WaitingOverlay,
            WaiveCoolingRights,
        },
        props: {
            isFromDirect: {
                type: Boolean,
                default: false,
            },
            handleProcessedPayment: {
                type: Function,
                default: () => {},
            },
            handleProcessedPaymentError: {
                type: Function,
                default: () => {},
            },
            isEditPayment: {
                type: Boolean,
                default: false,
            },
        },
        data: function () {
            return {
                payPalToken: null,
                paymentRequestData: {},
                bodyScrollPos: 0,
                orderSummaryConfig: OrderSummaryConfig,
                couponDiscountBillingDate: null,
                // for paypal only (for CC go to PaymentCreditCardTab)
                recaptchaAction: this.isSwitchPlan
                    ? RECAPTCHA_FORMS.FORM_SWITCH_PLAN
                    : RECAPTCHA_FORMS.FORM_PAYMENT,
                errorMessageElementId: 'summary-page-exsub-error-message',
                paymentMethodFormEnabled: false,
                activeTab: TAB.cc,
                isFTC: true,
            };
        },
        computed: {
            ...mapState('payment', {
                recurly: (state) => state.recurly,
                currencySymbol: (state) => state.currencySymbol,
                currencyCode: (state) => state.currencyCode,
            }),
            ...mapState(['promo']),
            ...mapGetters('plan', ['getSelectedPlan', 'isSwitchPlan']),
            ...mapGetters('user', ['isExSubscriber', 'isSubscriber']),
            ...mapGetters('ftc', [
                'getEditingStoredPaymentMethod',
                'getSubmitButtonState',
                'getTransactionProcessing',
                'getCoolingRightsWaived',
                'getNeedsCheckbox',
                'getPaymentMethodUpdateProcessing',
            ]),
            ...mapGetters('storedPaymentMethod', ['getBillingInfo', 'hasValidStoredPaymentMethod']),
            tab() {
                return TAB;
            },
            showCheckbox() {
                return this.getNeedsCheckbox;
            },
            submitCtaText() {
                return (
                    this.pageAttr?.billing_summary_cta_form_submit_ftc ?? this.$getLocale('buy_now')
                );
            },
            submitConfig() {
                const displayText = this.submitCtaText;
                return {
                    styleClass: [
                        'button',
                        this.ftcSubmitButtonState === ButtonState.Enabled ? 'primary' : 'disabled',
                    ],
                    displayText,
                    isDisabled: this.ftcSubmitButtonState !== ButtonState.Enabled,
                };
            },

            getTitle() {
                return this.isExSubscriber
                    ? this.pageAttr.explainer_description
                    : !this.isSwitchPlan
                      ? this.$getLocale('payment_method')
                      : this.pageAttr.aa_steps_header;
            },
            selectedPlan() {
                return this.$store.getters['plan/getSelectedPlan'];
            },
            getTitleClass() {
                return {
                    'payment-layout__step-title': true,
                    'payment-layout__spaced_from_icon': this.isSwitchPlan,
                };
            },
            currentPlan() {
                return this.$store.getters['plan/getCurrentPlan'];
            },
            waiveOfCoolingRightsCheckboxConfig() {
                let message = '';

                const { duration, period } = this.getStartingTrialDurationAndPeriod();

                const { promoDuration, promoPeriod, discountString } = this.getPromoDetails();

                //This is the standard message where we have a free trial and no promos
                message = this.pageAttr?.['aa_checkbox_free_trial_copy'];
                const hasValidCoupon = this.coupon && !this.coupon.errorMessage;

                //Also known as an ex-subscriber. In the switch plan also the trial is not applicable
                const hasNoFreeTrial =
                    ((this.isNoTrialUser || this.isSwitchPlan) && !this.coupon.trialString) ||
                    !this.plan?.trial;

                const hasFreeTrialExtension =
                    hasValidCoupon &&
                    this.coupon?.recurlyInfo?.discount?.type === COUPON_TYPES.FREE_TRIAL;

                const hasDiscount =
                    hasValidCoupon &&
                    (this.coupon?.recurlyInfo?.discount?.type === COUPON_TYPES.FIXED ||
                        this.coupon?.recurlyInfo?.discount?.type === COUPON_TYPES.PERCENT);

                const hasForeverDiscount =
                    hasValidCoupon && this.coupon?.recurlyInfo?.duration === 'forever';

                const hasSingleUseSingleCadenceDiscount =
                    hasValidCoupon &&
                    (this.coupon?.recurlyInfo?.single_use ||
                        this.coupon?.singleUse ||
                        promoDuration === 1);

                if (hasNoFreeTrial) {
                    message = this.pageAttr?.['aa_checkbox_no_intro_offer_copy'];
                    if (hasDiscount) {
                        message = this.pageAttr?.['aa_checkbox_promo_copy_with_no_free_trial_copy'];
                    }
                    if (hasSingleUseSingleCadenceDiscount) {
                        message =
                            this.pageAttr?.[
                                'aa_checkbox_promo_copy_with_no_free_trial_copy_single'
                            ];
                    }
                    if (hasForeverDiscount) {
                        message =
                            this.pageAttr?.[
                                'aa_checkbox_promo_copy_with_no_free_trial_copy_forever'
                            ];
                    }
                } else {
                    if (hasDiscount) {
                        message = this.pageAttr?.['aa_checkbox_free_trial_with_promo_copy'];
                    }
                    if (hasSingleUseSingleCadenceDiscount) {
                        message = this.pageAttr?.['aa_checkbox_free_trial_with_promo_copy_single'];
                    }
                    if (hasForeverDiscount) {
                        message = this.pageAttr?.['aa_checkbox_free_trial_with_promo_copy_forever'];
                    }
                    if (hasFreeTrialExtension) {
                        message = this.pageAttr?.['aa_checkbox_free_trial_copy'];
                    }
                }

                message = message
                    ?.replace(/{duration}/g, duration)
                    ?.replace(/{period}/g, period)
                    ?.replace(/{cadence}/g, this.selectedPlan?.priceUnit.toLowerCase())
                    ?.replace(/{price}/g, this.selectedPlan?.rawPrice)
                    ?.replace(/{promoPrice}/g, this.pricing?.price?.next?.subtotal)
                    ?.replace(/{promoPeriod}/g, promoPeriod)
                    ?.replace(/{promoDuration}/g, `${promoDuration} ${promoPeriod}`)
                    ?.replace(/{promoSavings}/g, discountString);

                if (this.showCheckbox) {
                    // Remove the prefix that is not needed when checkbox is shown
                    if (message) {
                        message = this.removeNonCheckboxLanguageFromMessage(message);
                    }
                }

                return {
                    type: 'checkbox',
                    elementId: 'ftcWaiveCoolingRights',
                    inputLabel: message,
                    wrapperClass:
                        'payment-layout__summary__waive-cooling-rights cbs-checkbox cbs-checkbox--waive-cooling-rights',
                    classList:
                        'payment-layout__summary__waive-cooling-rights__checkbox cbs-checkbox-input',
                    checked: this.getCoolingRightsWaived,
                };
            },
            ftcSubmitButtonState() {
                return this.getSubmitButtonState;
            },
            transactionIsProcessing() {
                return this.getTransactionProcessing;
            },
            getPaymentProcessingMessage() {
                return this.isEditPayment && this.getPaymentMethodUpdateProcessing
                    ? this.$getLocale('we_are_updating_your_payment_method')
                    : this.$getLocale('we_are_processing_your_subscription');
            },
            getLegalCopyForSwitchPlan() {
                let legalText = '';
                if (this.isSwitchPlan) {
                    if (this.getSelectedPlan.switchDirection) {
                        legalText =
                            this.getSelectedPlan.switchDirection === 'upgrade'
                                ? this.pageAttr?.['aa_switch_plan_upgrade']
                                : this.pageAttr?.['aa_switch_plan_downgrade'];
                    }
                }
                return legalText;
            },
            pageTypeForTracking() {
                let pageType = 'svod_payment';
                if (this.isSwitchPlan) {
                    pageType = this.isFromDirect
                        ? 'switchplan_directLink_payment'
                        : PAGE_TYPES.PLAN_CHANGE_CONFIRMATION;
                } else {
                    pageType = this.isFromDirect
                        ? 'purchase_directLink_payment'
                        : AA_PAGE_TYPES.PAYMENT;
                }
                return pageType;
            },
        },
        methods: {
            ...mapMutations('ftc', [
                'setEditingStoredPaymentMethod',
                'setPaymentMethod',
                'setFormValidated',
                'setTransactionProcessing',
                'setCoolingRightsWaived',
                'setFormErrorCode',
                'setPaymentMethodUpdateProcessing',
                'setTransactionComplete',
                'setPaymentMethodUpdateComplete',
                'setNeedsCheckbox',
            ]),
            ...mapMutations('storedPaymentMethod', ['setBillingInfo']),
            removeNonCheckboxLanguageFromMessage(message) {
                // The intent here is to remove text up until after the first double quoted phrase, and an optional comma and whitespace.
                const CHECKBOXLESS_PREFIX_REGEX = /^.*?"[^"]*",?\s*/;
                let result = message.replace(CHECKBOXLESS_PREFIX_REGEX, '');
                if (result.length >= 2) {
                    result = result.charAt(0).toUpperCase() + result.slice(1);
                }
                return result;
            },
            setPaymentMethodFormEnabled(event) {
                this.paymentMethodFormEnabled = event;
                this.setEditingStoredPaymentMethod(event);
            },
            setActiveTab(activeTab) {
                // this.activeTab = activeTab;
                this.setPaymentMethod(activeTab);
            },
            getStartingTrialDurationAndPeriod() {
                let duration = 7;
                let period = 'days';

                if (this.coupon?.recurlyInfo?.discount?.trial) {
                    const { amount, unit } = this.coupon.recurlyInfo.discount.trial;
                    duration = amount;
                    period = unit;
                    period = pluralizeDuration(duration, period);
                }

                return { duration, period };
            },
            checkIfCheckboxFlagShouldBeOverwritten() {
                this.setNeedsCheckbox(
                    (this.getBillingInfo?.ftcGuidelines?.needsFtcCheckbox ?? false) ||
                        !!this.$store.getters.featureIsActive(FTC_COMPLIANCE_CHECKBOX_ENABLED),
                );
            },

            getPromoDetails() {
                let promoDuration = 7;
                let promoPeriod = 'days';
                let discountString = '';

                const { planType } = this.plan;

                if (this.coupon?.recurlyInfo) {
                    ({
                        amount: promoDuration,
                        duration: promoPeriod,
                        discountString: discountString,
                    } = this.getPromoDetailsFromRecurlyCoupon(
                        this.coupon.recurlyInfo,
                        planType,
                        this.currencySymbol,
                        this.currencyCode,
                    ));
                }

                if (promoPeriod === 'single_use') {
                    promoDuration = 1;
                    promoPeriod =
                        this.selectedPlan.planType === Product.TYPE_MONTHLY
                            ? this.$getLocale('month')
                            : this.$getLocale('year');
                }

                return { promoDuration, promoPeriod, discountString };
            },
            handleWaiveOfCoolingChecked(newValue) {
                if (!this.showCheckbox) {
                    this.setCoolingRightsWaived(true);
                    return;
                }
                this.setCoolingRightsWaived(newValue);
            },
            emitActiveTab(tab) {
                this.activeTab = tab;
                this.$emit('update:activeTab', tab);
            },
            subscribeOrUpdate(e) {
                if (
                    this.ftcSubmitButtonState === ButtonState.Loading ||
                    this.ftcSubmitButtonState === ButtonState.Disabled
                ) {
                    return false;
                }

                this.setTransactionProcessing(true);

                if (
                    this.isExSubscriber &&
                    this.hasValidStoredPaymentMethod &&
                    !this.getEditingStoredPaymentMethod
                ) {
                    // resubmit their valid payment
                    // if ex-sub has entered in new payment they go through regular flow
                    return this.submitExSubscriberPayment(e);
                }

                if (this.isSwitchPlan) {
                    return this.submitOnClicked();
                } else {
                    return this.onSubmitPurchaseFTC(e);
                }
            },
            trackSwitchPlanSummaryState() {
                const trackingParams = {
                    productPricingPlan: this.plan.planType,
                    pickPlanType: this.plan.planTier,
                    pickPlanSku: this.plan.recurlyCode,
                    productCurrentSku:
                        this?.userProduct?.product_code ?? this.currentPlan?.recurlyCode,
                    productNewSku: this.plan.recurlyCode,
                    ctaText: this.submitCtaText,
                    pageType: this.pageTypeForTracking,
                    pageUrl: window.location.href,
                    paymentOnFile: this.hasValidStoredPaymentMethod ? '1' : '0',
                };

                trackingParams.purchaseProduct = this.plan.recurlyCode;
                trackingParams.purchaseProductName = this.plan.titleForTracking;
                trackingParams.purchasePrice = this.plan.rawPrice;
                trackingParams.productOfferPeriod =
                    this.coupon?.trialString ?? this.plan?.productOfferPeriod;
                trackingParams.purchaseQuantity = '1';
                trackingParams.purchasePromoCode = this.appliedCouponCode;

                const trackingServiceInstance = this.$trackingService ?? this.trackingService;

                trackingServiceInstance.trackState(trackingParams);
            },
            trackStateForSummary() {
                if (this.isSwitchPlan) {
                    this.trackSwitchPlanSummaryState();
                } else {
                    // tracking for non switch plan is handled inside payment.js (step.js -> enterRoute)
                }
            },
            trackCheckboxShown(showCheckbox) {
                const trackingParams = {
                    pageType: this.pageTypeForTracking,
                    pageUrl: window.location.href,
                    ftcCheckboxShown: showCheckbox ? '1' : '0',
                };

                const trackingServiceInstance = this.$trackingService ?? this.trackingService;
                if (showCheckbox) {
                    trackingServiceInstance.trackAction(
                        AA_ACTION_NAMES.FTC_CHECKBOX_SHOWN,
                        trackingParams,
                    );
                }
            },
        },

        watch: {
            coupon(newVal) {
                this.couponDiscountBillingDate = '';
                this.updateFirstBillingDate(newVal);
            },
            plan(newVal) {
                if (this.coupon?.recurlyInfo) {
                    this.handleCouponChange(this.coupon.recurlyInfo, true);
                    this.updateFirstBillingDate(this.coupon);
                }
            },
            showCheckbox(newVal) {
                trackCheckboxShown(newVal);
            },
        },
        async mounted() {
            this.setTransactionProcessing(false);
            if (this.$route.query?.notification) {
                this.openNotification(
                    new NotificationBarModel({
                        success: true,
                        autoClose: false,
                        message: this.$getLocale(this.$route.query.notification),
                    }),
                );
            }

            if (
                this.isSwitchPlan ||
                this.$store.getters.featureIsActive(PAYMENT_EXSUB_BILLING_SUMMARY)
            ) {
                await fetchStoredPaymentIfEmpty(this.$store);
            }
            this.trackStateForSummary();
            this.checkIfCheckboxFlagShouldBeOverwritten();

            if (!this.showCheckbox) {
                this.setCoolingRightsWaived(true);
            } else {
                // prevents the flag from remaining true if the user navigates back to pick a plan and returns to the payment page
                this.setCoolingRightsWaived(false);
            }

            this.trackCheckboxShown(this.showCheckbox);
        },
    };
</script>
